import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getNotificationRepository } from "@/infrastructure/di";
import type { NotificationFilters, NotificationListResponse } from "@/domain";
import { queryKeys } from "../keys";

/** Notifications are dynamic data — keep stale time short for near-realtime updates */
const NOTIFICATION_STALE_TIME = 30 * 1000; // 30 seconds
const PAGE_SIZE = 10;

export const useNotifications = (
	orgId: string,
	filters?: NotificationFilters,
) => {
	return useInfiniteQuery<NotificationListResponse>({
		queryKey: queryKeys.notifications.list(orgId, filters),
		queryFn: ({ pageParam }) =>
			getNotificationRepository().getNotifications({
				...filters,
				limit: PAGE_SIZE,
				offset: pageParam as number,
			}),
		initialPageParam: 0,
		getNextPageParam: (lastPage) => {
			const nextOffset = lastPage.offset + lastPage.notifications.length;
			return nextOffset < lastPage.total_count ? nextOffset : undefined;
		},
		staleTime: NOTIFICATION_STALE_TIME,
		enabled: !!orgId,
	});
};

export const useNotificationStats = (orgId: string) => {
	return useQuery({
		queryKey: queryKeys.notifications.stats(orgId),
		queryFn: () => getNotificationRepository().getNotificationStats(),
		staleTime: NOTIFICATION_STALE_TIME,
		enabled: !!orgId,
	});
};
